/* eslint-disable max-lines */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { TableUtility } from '@utility/dynamic-table';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { toast } from 'react-toastify';
import { MODULE_NAMES } from '../../../Constants/modules';
import {
  CANCEL_TEXT,
  ENABLE_DISABLE_CLIENT_DIALOG,
} from '../../../Constants/strings';
import { useAuth } from '../../../Hooks/useAuth';
import UseAxios from '../../../Api/http/http';
import {
  createFinanceClient,
  financeClientDetailsList,
  updateFinanceClient,
} from '../../../Api/request/financeClientDetails';
import FormDialog from '../../../Components/Modal/modal';
import ControlledSwitches from '../../../Components/Switch/switch';
import EditActionIcon from '../../../Assets/images/svg/dar_edit.svg';
import { ReactComponent as ErrorInfoIcon } from '../../../Assets/images/svg/errinfo.svg';
import alertIcon from '../../../Assets/images/svg/alert_icon.svg';
import { clientDetailsTableConfig } from './clientDetailsTableConfig';
import './financeclientdetails.scss';

const INITIAL_CLIENT_FORM = {
  clientName: '',
  clientStatus: '',
};

const formatExportDate = () => new Date()
  .toISOString()
  .split('T')[0];

const renderClientModal = ({
  clientForm,
  dialogMode,
  handleSubmitClient,
  handleClientFormChange,
  handleCloseClientDialog,
  isStatusHelpOpen,
  duplicateClientMessage,
  isSubmitDisabled,
  fieldErrors,
  setIsStatusHelpOpen,
}) => createPortal(
  <div className="finance-client-details-modal-backdrop">
    {duplicateClientMessage && (
      <div className="finance-client-details-warning-toast">
        <div className="finance-client-details-warning-toast__content">
          <ErrorInfoIcon className="finance-client-details-warning-toast__icon" />
          <span className="finance-client-details-warning-toast__text">
            {duplicateClientMessage}
          </span>
        </div>
      </div>
    )}
    <form className="finance-client-details-modal" onSubmit={handleSubmitClient}>
      <div className="finance-client-details-modal__header">
        {dialogMode === 'edit' ? 'Edit Client' : 'Add Client'}
      </div>
      <div className="finance-client-details-modal__body">
        <div className="finance-client-details-modal__field">
          <label
            htmlFor="finance-client-name"
            className={fieldErrors?.clientName ? 'is-error' : ''}
          >
            Client Name<span className="required-asterisk">*</span>
          </label>
          <input
            id="finance-client-name"
            name="clientName"
            type="text"
            placeholder="Enter Client Name"
            value={clientForm.clientName}
            onChange={handleClientFormChange}
            className={fieldErrors?.clientName ? 'is-error' : ''}
          />
        </div>
        <div className="finance-client-details-modal__field">
          <label
            htmlFor="finance-client-status"
            className={fieldErrors?.clientStatus ? 'is-error' : ''}
          >
            Client Status<span className="required-asterisk">*</span>
            <button
              type="button"
              className="finance-client-details-modal__info"
              aria-label="Client status help"
              onClick={() => setIsStatusHelpOpen((isOpen) => !isOpen)}
            >
              i
            </button>
            {isStatusHelpOpen && (
              <span className="finance-client-details-modal__help-message">
                <span>Select &quot;Active&quot; if the client is active.</span>
                <span>Select &quot;Inactive&quot; if the client is inactive.</span>
              </span>
            )}
          </label>
          <select
            id="finance-client-status"
            name="clientStatus"
            className={[
              clientForm.clientStatus === '' ? 'is-placeholder' : '',
              fieldErrors?.clientStatus ? 'is-error' : '',
            ].filter(Boolean).join(' ')}
            value={clientForm.clientStatus}
            onChange={handleClientFormChange}
          >
            <option value="">Select</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
        </div>
      </div>
      <div className="finance-client-details-modal__footer">
        <button
          type="button"
          className="finance-client-details-modal__btn finance-client-details-modal__btn--ghost"
          onClick={handleCloseClientDialog}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="finance-client-details-modal__btn finance-client-details-modal__btn--primary"
          disabled={isSubmitDisabled}
        >
          {dialogMode === 'edit' ? 'Update' : 'Add'}
        </button>
      </div>
    </form>
  </div>,
  document.body,
);

const getAxiosResultMessage = (result) => (
  result?.data?.response?.data?.message
  || result?.data?.response?.data?.externalMessage
  || result?.data?.message
  || ''
);

const getFinanceClientPermissions = (permissions = {}) => (
  permissions[MODULE_NAMES.FINANCE_CLIENT_DETAILS]
  || permissions[MODULE_NAMES.FINANCE_MILESTONE]
  || []
);

const useFinanceClientDetailsData = ({ hasAccess }) => {
  const [clients, setClients] = useState([]);
  const [__isLoading, setIsLoading] = useState(false);

  const loadClients = useCallback(async () => {
    if (!hasAccess('read')) {
      setClients([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await UseAxios(financeClientDetailsList(), setIsLoading);
      const rows = response?.data?.data?.tableData || response?.data?.clientDetails || [];
      setClients(rows.map((row) => ({
        ...row,
        id: row.clientId,
      })));
    } catch (_error) {
      setClients([]);
    } finally {
      setIsLoading(false);
    }
  }, [hasAccess]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  return {
    clients,
    __isLoading,
    setIsLoading,
    loadClients,
  };
};

const buildFinanceClientsTableData = (clients) => (
  clients.map((client, index) => ({
    ...client,
    slNo: index + 1,
    clientStatusLabel: Number(client?.clientStatus) === 1 ? 'Active' : 'Inactive',
    action: (
      <div
        className="finance-client-details-actions-cell"
        onClick={(event) => event.stopPropagation()}
      >
        {client.action}
      </div>
    ),
  }))
);

const buildFinanceClientConfig = ({
  userProfile,
  showAddNew,
  total,
  onAddClick,
}) => ({
  generalSettings: {
    enableApiFiltering: false,
    showPagination: false,
    showToolBar: true,
    apiEndpoints: {
      download: {
        enabled: true,
        filename: () => `client-details-${formatExportDate()}`,
        sheetName: () => `client-details-${formatExportDate()}`,
        reportTitle: () => `Client Details - ${formatExportDate()}`,
        transformResponse: (rows) => ({
          tableConfiguration: [
            { field: 'slNo', header: 'SI.No', numeric: false },
            { field: 'clientId', header: 'Client ID', numeric: false },
            { field: 'clientName', header: 'Client Name' },
            { field: 'clientStatusLabel', header: 'Client Status' },
            { field: 'totalProjectsLinked', header: 'Total Projects Linked', numeric: false },
          ],
          tableData: (rows || []).map((row, index) => ({
            slNo: index + 1,
            clientId: row?.clientId === null || row?.clientId === undefined ? '' : Number(row.clientId),
            clientName: row?.clientName ?? '',
            clientStatusLabel: row?.clientStatusLabel ?? '',
            totalProjectsLinked: row?.totalProjectsLinked === null || row?.totalProjectsLinked === undefined
              ? 0
              : (
                Number.isFinite(Number(row.totalProjectsLinked))
                  ? Math.trunc(Number(row.totalProjectsLinked))
                  : row.totalProjectsLinked
              ),
            format: {
              slNo: '0',
              clientId: '0',
              totalProjectsLinked: '0',
            },
          })),
        }),
      },
    },
    userInfo: {
      emailId: userProfile?.email || '',
      permissions: userProfile?.permissions || {},
      roleDetails: userProfile?.roleDetails || [],
      employeeId: userProfile?.employeeId || '',
      globalColors: userProfile?.siteConfiguration?.global_colors,
    },
    handlers: {
      handleAddIconClick: onAddClick,
    },
    toolBarSettings: {
      showAddNew,
      showFilterOption: false,
      showDownloadExcel: true,
      showDownloadCsv: false,
      showExpandCollapse: false,
      showColumnCustomize: false,
      showHelp: false,
      showSearch: false,
      showSaveView: false,
      showReset: true,
      showToolBarHeading: true,
      toolBarHeading: 'Client Details',
      downloadExcelSheetName: 'Client Details',
    },
  },
  showToolBar: true,
  tableSettings: {
    size: 'small',
    enableTreeTable: false,
    showExpandCollapse: false,
    tableConfig: clientDetailsTableConfig,
    loaderSkeleton: {
      rows: 10,
      bodyCells: 1,
      columns: clientDetailsTableConfig.length,
    },
    paginationConfig: {
      page: 1,
      limit: total,
      total,
      totalPages: total > 0 ? 1 : 0,
      rowsPerPageOptions: [total || 1],
    },
    tableScrollHeight: 'calc(100vh - 140px)',
    maxFrozenColumns: {
      desktop: 1,
      tablet: 1,
      mobile: 1,
    },
  },
});

const hideRowsPerPageDropdown = (rootSelector) => {
  if (typeof document === 'undefined') return;

  const root = document.querySelector(rootSelector);
  if (!root) return;

  root.querySelectorAll(
    '.custom-paginator .p-paginator-rpp-options,'
      + ' .custom-paginator .p-dropdown.p-component.p-inputwrapper,'
      + ' .custom-paginator .p-dropdown.p-component.p-inputwrapper-filled,'
      + ' .custom-paginator .p-dropdown-trigger,'
      + ' .p-paginator-rpp-options,'
      + ' .p-paginator .p-dropdown,'
      + ' .p-paginator .p-dropdown-trigger',
  ).forEach((node) => {
    node.style.display = 'none';
  });
};

const useFinanceClientDialog = ({
  clients,
  setIsLoading,
  loadClients,
}) => {
  const [clientDialog, setClientDialog] = useState({
    mode: 'add',
    selectedClientId: null,
    isOpen: false,
  });
  const [isStatusHelpOpen, setIsStatusHelpOpen] = useState(false);
  const [duplicateClientMessage, setDuplicateClientMessage] = useState('');
  const [clientForm, setClientForm] = useState(INITIAL_CLIENT_FORM);
  const [fieldErrors, setFieldErrors] = useState({
    clientName: false,
    clientStatus: false,
  });
  const duplicateToastTimerRef = useRef(null);

  useEffect(() => {
    if (!duplicateClientMessage) {
      return undefined;
    }

    duplicateToastTimerRef.current = window.setTimeout(() => {
      setDuplicateClientMessage('');
    }, 3000);

    return () => window.clearTimeout(duplicateToastTimerRef.current);
  }, [duplicateClientMessage]);

  const handleOpenAddClient = useCallback(() => {
    setClientForm(INITIAL_CLIENT_FORM);
    setIsStatusHelpOpen(false);
    setDuplicateClientMessage('');
    setFieldErrors({ clientName: false, clientStatus: false });
    setClientDialog({
      mode: 'add',
      selectedClientId: null,
      isOpen: true,
    });
  }, []);

  const handleOpenEditClient = useCallback((client) => {
    setClientForm({
      clientName: client.clientName,
      clientStatus: String(client.clientStatus),
    });
    setIsStatusHelpOpen(false);
    setDuplicateClientMessage('');
    setFieldErrors({ clientName: false, clientStatus: false });
    setClientDialog({
      mode: 'edit',
      selectedClientId: client.clientId,
      isOpen: true,
    });
  }, []);

  const handleCloseClientDialog = useCallback(() => {
    setClientForm(INITIAL_CLIENT_FORM);
    setIsStatusHelpOpen(false);
    setDuplicateClientMessage('');
    setFieldErrors({ clientName: false, clientStatus: false });
    setClientDialog({
      mode: 'add',
      selectedClientId: null,
      isOpen: false,
    });
  }, []);

  const handleClientFormChange = useCallback((event) => {
    const { name, value } = event.target;
    setClientForm((currentForm) => ({
      ...currentForm,
      [name]: value,
    }));

    setFieldErrors((currentErrors) => {
      if (!currentErrors[name]) return currentErrors;
      if (name === 'clientName' && value.trim()) return { ...currentErrors, clientName: false };
      if (name === 'clientStatus' && value !== '') return { ...currentErrors, clientStatus: false };
      return currentErrors;
    });
  }, []);

  const selectedClient = useMemo(() => clients.find((client) => (
    client.clientId === clientDialog.selectedClientId
  )), [clientDialog.selectedClientId, clients]);

  const isSubmitDisabled = useMemo(() => {
    if (clientDialog.mode !== 'edit' || !selectedClient) {
      return false;
    }

    return (
      clientForm.clientName.trim() === selectedClient.clientName.trim()
      && String(clientForm.clientStatus) === String(selectedClient.clientStatus)
    );
  }, [clientDialog.mode, clientForm, selectedClient]);

  const handleSubmitClient = useCallback((event) => {
    event.preventDefault();
    const clientName = clientForm.clientName.trim();

    if (!clientName || clientForm.clientStatus === '') {
      setFieldErrors({
        clientName: !clientName,
        clientStatus: clientForm.clientStatus === '',
      });
      toast.error('Please enter client name and client status.');
      return;
    }

    if (clientDialog.mode === 'edit') {
      UseAxios(
        updateFinanceClient(
          clientDialog.selectedClientId,
          clientName,
          clientForm.clientStatus,
        ),
        setIsLoading,
      )
        .then((response) => {
          const responseMessage = getAxiosResultMessage(response);
          if (responseMessage) {
            setDuplicateClientMessage(responseMessage);
            return;
          }

          toast.success('Client updated successfully.');
          handleCloseClientDialog();
          loadClients();
        });
      return;
    }

    UseAxios(
      createFinanceClient(clientName, clientForm.clientStatus),
      setIsLoading,
    )
      .then((response) => {
        const responseMessage = getAxiosResultMessage(response);
        if (responseMessage) {
          setDuplicateClientMessage(responseMessage);
          return;
        }
        toast.success('Client added successfully.');
        handleCloseClientDialog();
        loadClients();
      });
  }, [
    clientDialog.mode,
    clientDialog.selectedClientId,
    clientForm.clientName,
    clientForm.clientStatus,
    handleCloseClientDialog,
    loadClients,
    setIsLoading,
  ]);

  return {
    clientDialog,
    clientForm,
    isStatusHelpOpen,
    duplicateClientMessage,
    isSubmitDisabled,
    fieldErrors,
    handleOpenAddClient,
    handleOpenEditClient,
    handleCloseClientDialog,
    handleClientFormChange,
    handleSubmitClient,
    setIsStatusHelpOpen,
  };
};

const FinanceClientDetails = () => {
  const { userProfile } = useAuth();
  const permission = getFinanceClientPermissions(userProfile?.permissions);
  const hasAccess = useCallback((action) => permission.includes(action), [permission]);
  const {
    clients,
    setIsLoading,
    loadClients,
  } = useFinanceClientDetailsData({ hasAccess });

  const [toggleClient, setToggleClient] = useState(null);
  const toggleContent = useMemo(() => {
    const isCurrentlyActive = Boolean(toggleClient?.clientStatus);
    return isCurrentlyActive
      ? ENABLE_DISABLE_CLIENT_DIALOG.DISABLE
      : ENABLE_DISABLE_CLIENT_DIALOG.ENABLE;
  }, [toggleClient]);

  const {
    clientDialog,
    clientForm,
    isStatusHelpOpen,
    duplicateClientMessage,
    isSubmitDisabled,
    fieldErrors,
    handleOpenAddClient,
    handleOpenEditClient,
    handleCloseClientDialog,
    handleClientFormChange,
    handleSubmitClient,
    setIsStatusHelpOpen,
  } = useFinanceClientDialog({
    clients,
    setIsLoading,
    loadClients,
  });

  const handleToggleClient = useCallback((client) => {
    setToggleClient(client);
  }, []);

  const handleCloseToggleDialog = useCallback(() => {
    setToggleClient(null);
  }, []);

  const handleConfirmToggleClient = useCallback(async () => {
    if (!toggleClient) return;

    const nextStatus = Number(toggleClient.clientStatus) ? 0 : 1;
    const result = await UseAxios(
      updateFinanceClient(
        toggleClient.clientId,
        toggleClient.clientName,
        String(nextStatus),
      ),
      setIsLoading,
    );

    const message = getAxiosResultMessage(result);
    if (message) {
      toast.error(message);
      return;
    }

    toast.success(toggleContent.RESPONSE_SUCCESS);
    handleCloseToggleDialog();
    loadClients();
  }, [
    loadClients,
    setIsLoading,
    toggleClient,
    toggleContent.RESPONSE_SUCCESS,
  ]);

  const tableData = useMemo(
    () => buildFinanceClientsTableData(
      clients.map((client) => ({
        ...client,
        action: (
          <div className="finance-client-details-action-cell-inner">
            <ControlledSwitches
              checked={Boolean(client.clientStatus)}
              disabled={!hasAccess('update')}
              switchCss="switchCss"
              handleChange={() => handleToggleClient(client)}
            />
            <button
              type="button"
              className="finance-client-details-action-btn"
              title="Edit Client"
              aria-label={`Edit ${client.clientName}`}
              disabled={!hasAccess('update')}
              onClick={() => handleOpenEditClient(client)}
            >
              <img src={EditActionIcon} alt="" />
            </button>
          </div>
        ),
      })),
    ),
    [clients, handleOpenEditClient, handleToggleClient, hasAccess],
  );

  const configuration = useMemo(
    () => buildFinanceClientConfig({
      userProfile,
      showAddNew: hasAccess('create'),
      total: tableData.length,
      onAddClick: handleOpenAddClient,
    }),
    [handleOpenAddClient, hasAccess, tableData.length, userProfile],
  );

  useEffect(() => {
    hideRowsPerPageDropdown('.finance-client-details-tu');
  }, [tableData.length]);

  if (!hasAccess('read')) {
    return <div>You do not have permission to view client details.</div>;
  }

  return (
    <div className="finance-client-details-tu">
      <TableUtility
        configuration={configuration}
        data={tableData}
      />
      {clientDialog.isOpen && renderClientModal({
        clientForm,
        dialogMode: clientDialog.mode,
        handleSubmitClient,
        handleClientFormChange,
        handleCloseClientDialog,
        isStatusHelpOpen,
        duplicateClientMessage,
        isSubmitDisabled,
        fieldErrors,
        setIsStatusHelpOpen,
      })}
      <FormDialog
        open={Boolean(toggleClient)}
        title={toggleContent.TITLE_TEXT}
        dialogCss="dialog-message"
        dialogPaperCss="client-enable-dialog-paper"
        paperprops={{}}
        dialogBodycontent={(
          <div className="dialog-body-text">
            <div className="dialog-alert-icon">
              <img alt="Alert Icon" className="icon-image" src={alertIcon} />
            </div>
            <div className="dialog-message-text">{toggleContent.MESSAGE_TEXT}</div>
          </div>
        )}
        button1={CANCEL_TEXT}
        cancelButtonId={`cancel-${toggleContent.BUTTON_ID}`}
        button2={toggleContent.BUTTON_TEXT}
        submitButtonId={toggleContent.BUTTON_ID}
        button1Action={handleCloseToggleDialog}
        button2Action={handleConfirmToggleClient}
      />
    </div>
  );
};

export default FinanceClientDetails;
